import { Box, Stack, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import { generateClient } from 'aws-amplify/api';
import { motion } from 'framer-motion';
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { type Schema } from '../../../../../../amplify/data/resource';

// @ts-ignore
type File = Schema['File']['type'];

// @ts-ignore
type Employee = Schema['Employee']['type'];

// @ts-ignore
type AdminLog = Schema['AdminLog']['type'];

interface Props {
  open: boolean;
  fileId: string;
}

const apiClient = generateClient<Schema>();

const FileDetails: React.FC<Props> = ({ open, fileId }) => {
  const navigate = useNavigate();

  const [logs, setLogs] = React.useState<AdminLog[]>([]);

  useEffect(() => {
    const getLogs = async () => {
      try {
        const response = await apiClient.models.AdminLog.listAdminLogByFileIdAndCreatedAt({fileId: fileId})
        setLogs(response.data || []);
      } catch (error) {
        console.error('Error fetching logs:', error);
      }
    };
    getLogs();
  }, [fileId]);

  // Helper function to format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('he-IL');
  };

  // Helper function to format time
  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' });
  };

  // Helper function to get status color and text
  const getStatusDisplay = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return { color: '#94C729', text: 'הושלם' };
      case 'PENDING':
        return { color: '#F59E0B', text: 'בתהליך' };
      case 'REJECTED':
        return { color: '#EF4444', text: 'נדחה' };
      default:
        return { color: '#6B7280', text: status };
    }
  };

  // Helper function to get action display text
  const getActionDisplay = (action: string) => {
    switch (action) {
      case 'INTEGRATED_PAYMENT_STARTED':
        return 'תשלום מתחיל';
      case 'PAYMENT_COMPLETED':
        return 'תשלום הושלם';
      case 'PAYMENT_FAILED':
        return 'תשלום נכשל';
      default:
        return action || 'תשלום';
    }
  };

  return (
    <>
      {open && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{
            duration: 0.3,
            ease: 'easeInOut',
          }}
          style={{ overflow: 'hidden', width: '100%' }}
        >
          <Box
            sx={{
              backgroundColor: '#F2F4F7',
              borderRadius: '10px',
              p: '20px',
              boxSizing: 'border-box',
              width: '100%',
              boxShadow: 'none',
              textAlign: 'right',
              verticalAlign: 'bottom',
            }}
          >
            <Box sx={{ direction: 'rtl' }}>
              <TableContainer
                component={Paper}
                sx={{
                  backgroundColor: 'transparent',
                  boxShadow: 'none',
                  overflowY: 'auto',
                }}
              >
                <Table
                  sx={{
                    backgroundColor: 'transparent',
                    borderCollapse: 'separate',
                    borderSpacing: '0 10px',
                  }}
                >
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'transparent' }}>
                      <TableCell
                        sx={{
                          padding: '10px 20px',
                          borderBottom: 'none',
                          textAlign: 'right',
                          backgroundColor: 'transparent',
                        }}
                      >
                        <Typography
                          fontSize="14px"
                          fontWeight="700"
                          fontFamily={'AlmoniDL'}
                        >
                          תאריך
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: '10px 20px',
                          borderBottom: 'none',
                          textAlign: 'right',
                          backgroundColor: 'transparent',
                        }}
                      >
                        <Typography
                          fontSize="14px"
                          fontWeight="700"
                          fontFamily={'AlmoniDL'}
                        >
                          שעה
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: '10px 20px',
                          borderBottom: 'none',
                          textAlign: 'right',
                          backgroundColor: 'transparent',
                        }}
                      >
                        <Typography
                          fontSize="14px"
                          fontWeight="700"
                          fontFamily={'AlmoniDL'}
                        >
                          פעולה
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: '10px 20px',
                          borderBottom: 'none',
                          textAlign: 'right',
                          backgroundColor: 'transparent',
                        }}
                      >
                        <Typography
                          fontSize="14px"
                          fontWeight="700"
                          fontFamily={'AlmoniDL'}
                        >
                          סטטוס
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: '10px 20px',
                          borderBottom: 'none',
                          textAlign: 'right',
                          backgroundColor: 'transparent',
                        }}
                      >
                        <Typography
                          fontSize="14px"
                          fontWeight="700"
                          fontFamily={'AlmoniDL'}
                        >
                          פירוט
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: '10px 20px',
                          borderBottom: 'none',
                          textAlign: 'right',
                          backgroundColor: 'transparent',
                        }}
                      >
                        <Typography
                          fontSize="14px"
                          fontWeight="700"
                          fontFamily={'AlmoniDL'}
                        >
                          בנק משלם
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: '10px 20px',
                          borderBottom: 'none',
                          textAlign: 'right',
                          backgroundColor: 'transparent',
                        }}
                      >
                        <Typography
                          fontSize="14px"
                          fontWeight="700"
                          fontFamily={'AlmoniDL'}
                        >
                          בנק מקבל
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: '10px 20px',
                          borderBottom: 'none',
                          textAlign: 'right',
                          backgroundColor: 'transparent',
                        }}
                      >
                        <Typography
                          fontSize="14px"
                          fontWeight="700"
                          fontFamily={'AlmoniDL'}
                        >
                          אסמכתה
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {logs.length > 0 ? (
                      logs.map((log) => (
                        <PaymentTableRow key={log.id} log={log} />
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={8} sx={{ textAlign: 'center', padding: '40px' }}>
                          <Typography
                            fontSize="14px"
                            fontFamily={'AlmoniDL'}
                            color="#6B7280"
                          >
                            לא נמצאו רשומות
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>
        </motion.div>
      )}
    </>
  );
};

export default FileDetails;

// Компонент строки таблицы с реальными данными
interface PaymentTableRowProps {
  log: AdminLog;
}

const PaymentTableRow: React.FC<PaymentTableRowProps> = ({ log }) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('he-IL');
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' });
  };

  const getStatusDisplay = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return { color: '#94C729', text: 'הושלם' };
      case 'PENDING':
        return { color: '#F59E0B', text: 'בתהליך' };
      case 'REJECTED':
        return { color: '#EF4444', text: 'נדחה' };
      default:
        return { color: '#6B7280', text: status };
    }
  };

  const getActionDisplay = (action: string) => {
    switch (action) {
      case 'INTEGRATED_PAYMENT_STARTED':
        return 'תשלום מתחיל';
      case 'PAYMENT_COMPLETED':
        return 'תשלום הושלם';
      case 'PAYMENT_FAILED':
        return 'תשלום נכשל';
      default:
        return action || 'תשלום';
    }
  };

  const statusDisplay = getStatusDisplay(log.status || '');

  return (
    <TableRow
      sx={{
        backgroundColor: '#FFF',
        borderRadius: '10px',
        overflow: 'hidden',
        '&:last-child td, &:last-child th': { border: 0 },
        boxShadow: 'none',
        textAlign: 'right',
        verticalAlign: 'middle',
      }}
    >
      <TableCell
        sx={{
          padding: '20px',
          borderBottom: 'none',
          borderTopRightRadius: '10px',
          borderBottomRightRadius: '10px',
        }}
      >
        <Typography
          fontWeight={'700'}
          fontSize={'14px'}
          fontFamily={'AlmoniDL'}
          textAlign={'right'}
        >
          {formatDate(log.createdAt)}
        </Typography>
      </TableCell>
      
      <TableCell
        sx={{
          padding: '20px',
          borderBottom: 'none',
          textAlign: 'right',
        }}
      >
        <Typography
          fontWeight={'700'}
          fontSize={'14px'}
          fontFamily={'Montserrat'}
        >
          {formatTime(log.createdAt)}
        </Typography>
      </TableCell>
      
      <TableCell
        sx={{
          padding: '20px',
          borderBottom: 'none',
          textAlign: 'right',
        }}
      >
        <Typography
          fontWeight={'700'}
          fontSize={'14px'}
          fontFamily={'AlmoniDL'}
        >
          {getActionDisplay(log.action || '')}
        </Typography>
      </TableCell>
      
      <TableCell
        sx={{
          padding: '20px',
          borderBottom: 'none',
          textAlign: 'right',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Box
            sx={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: statusDisplay.color,
            }}
          />
          <Typography
            fontWeight={'700'}
            fontSize={'14px'}
            fontFamily={'AlmoniDL'}
          >
            {statusDisplay.text}
          </Typography>
        </Box>
      </TableCell>
      
      <TableCell
        sx={{
          padding: '20px',
          borderBottom: 'none',
          textAlign: 'right',
        }}
      >
        <Typography
          fontWeight={'700'}
          fontSize={'14px'}
          fontFamily={'AlmoniDL'}
        >
          {log.description || '-'}
        </Typography>
      </TableCell>
      
      <TableCell
        sx={{
          padding: '20px',
          borderBottom: 'none',
          textAlign: 'right',
        }}
      >
        <Typography
          fontWeight={'700'}
          fontSize={'14px'}
          fontFamily={'Montserrat'}
        >
          {log.debtor || '-'}
        </Typography>
      </TableCell>
      
      <TableCell
        sx={{
          padding: '20px',
          borderBottom: 'none',
          textAlign: 'right',
        }}
      >
        <Typography
          fontWeight={'700'}
          fontSize={'14px'}
          fontFamily={'Montserrat'}
        >
          {log.creditor || '-'}
        </Typography>
      </TableCell>
      
      <TableCell
        sx={{
          padding: '20px',
          borderBottom: 'none',
          textAlign: 'right',
          borderTopLeftRadius: '10px',
          borderBottomLeftRadius: '10px',
        }}
      >
        <Typography
          fontWeight={'700'}
          fontSize={'14px'}
          fontFamily={'Montserrat'}
        >
          {log.referenceNumber || '-'}
        </Typography>
      </TableCell>
    </TableRow>
  );
};
